import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    window.addEventListener('orientationchange', this.#handleOrientationChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#createObserver();

    if (!this.#isTouchDevice()) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('orientationchange', this.#handleOrientationChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {IntersectionObserver | null}
   */
  #observer = null;

  #wasVisible = false;

  #isTouchDevice() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  #createObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        const isVisible = entry.isIntersecting;

        if (isVisible && !this.#wasVisible && window.innerWidth <= 749) {
          this.#refreshMarquee();
        }

        this.#wasVisible = isVisible;
      },
      {
        root: null,
        threshold: 0.01,
      }
    );

    this.#observer.observe(this);
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 55;
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;

    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    const count = marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    const speed = Math.sqrt(count) * speedFactor;

    return speed;
  }

  #handleResize = debounce(() => {
    this.#refreshMarquee();
  }, 250);

  #handleOrientationChange = debounce(() => {
    this.#refreshMarquee();
  }, 300);

  #handleVisibilityChange = () => {
    if (!document.hidden) {
      this.#refreshMarquee();
    }
  };

  #handlePageShow = () => {
    this.#refreshMarquee();
  };

  #refreshMarquee() {
    const { marqueeItems } = this.refs;

    if (!marqueeItems || !marqueeItems[0]) return;

    this.#removeExtraRepeatedItems();

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    this.#addRepeatedItems(newNumberOfCopies);

    this.#duplicateContent();
    this.#setSpeed();
    this.#forceRestartAnimation();
  }

  #forceRestartAnimation() {
    const wrapper = this.refs.wrapper;
    if (!wrapper) return;

    this.#animation?.cancel();
    this.#animation = null;

    wrapper.style.animation = 'none';
    wrapper.style.transform = 'translate3d(0, 0, 0)';

    requestAnimationFrame(() => {
      void wrapper.offsetWidth;

      requestAnimationFrame(() => {
        wrapper.style.animation = '';
        wrapper.style.transform = '';

        const animations = wrapper.getAnimations();

        for (const animation of animations) {
          animation.currentTime = 0;
          animation.updatePlaybackRate(1);
          animation.play();
        }
      });
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeExtraRepeatedItems() {
    const { content, marqueeItems } = this.refs;

    if (!content || !marqueeItems || !marqueeItems[0]) return;

    while (content.children.length > 1) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

/**
 * Animate a numeric property smoothly.
 *
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}